import React, { useState, useEffect, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { 
    ArrowLeft, 
    Heart, 
    Share2, 
    MessageCircle, 
    User, 
    Send, 
    Sparkles,
    Play,
    Pause,
    Headphones
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import toast from "react-hot-toast";
import api from "../api/axios";
import tracker from "../utils/sanctuary-tracker";
import SEO from "../components/SEO";
import { useAuth } from "../context/AuthContext";
import { useLanguage } from "../context/LanguageContext";
import { CONFIG } from "../config";

export default function ArticleDetail() {
    const { idOrSlug } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const { language, switchLanguage, t } = useLanguage();
    const [article, setArticle] = useState(null);
    const [loading, setLoading] = useState(true);
    const [commentText, setCommentText] = useState("");
    const [submittingComment, setSubmittingComment] = useState(false);
    const [recommendations, setRecommendations] = useState([]);
    const [hasLiked, setHasLiked] = useState(false);

    const fetchArticle = useCallback(async () => {
        try {
            setLoading(true);
            const res = await api.get(`/articles/${idOrSlug}?lang=${language}`);
            setArticle(res.data);
            
            // 🧘 AYI: Track Read
            tracker.trackWisdom(res.data.id, res.data.title);
            
            // Fetch related wisdom (also localized)
            const relRes = await api.get(`/articles?category=${res.data.categoryId}&limit=4&lang=${language}`);
            setRecommendations(relRes.data.filter(a => a.id !== res.data.id));
        } catch (err) {
            console.error("Wisdom fetch failure:", err);
            toast.error("The transmission was interrupted");
            navigate("/explore");
        } finally {
            setLoading(false);
        }
    }, [idOrSlug, navigate, language]);

    useEffect(() => {
        fetchArticle();
        window.scrollTo(0, 0);
    }, [fetchArticle]);

    const handleLike = async () => {
        if (hasLiked) return;
        try {
            setHasLiked(true);
            const res = await api.post(`/articles/${article.id}/like`);
            setArticle(prev => ({ ...prev, likes: res.data.likes }));
            
            // 🧘 AYI: Track Like
            tracker.track('WISDOM_LIKE', { articleId: article.id, title: article.title });
            
            toast.success("Resonance recorded", { icon: "❤️" });
        } catch (err) {
            setHasLiked(false);
            toast.error("The heart remains quiet for now");
        }
    };

    const handleShare = async () => {
        const shareData = {
            title: article?.title,
            text: article?.excerpt || "Explore this wisdom on Antaryatra",
            url: window.location.href
        };

        try {
            if (navigator.share) {
                await navigator.share(shareData);
            } else {
                await navigator.clipboard.writeText(window.location.href);
                toast.success("Sanctuary link copied to clipboard");
            }

            // 🧘 AYI: Track Share
            tracker.track('WISDOM_SHARE', { articleId: article.id, title: article.title });
        } catch (err) {
            console.error(err);
        }
    };

    const handlePostComment = async () => {
        if (!commentText.trim()) return;
        try {
            setSubmittingComment(true);
            await api.post(`/articles/${article.id}/comments`, { content: commentText });
            setCommentText("");
            fetchArticle();
            
            // 🧘 AYI: Track Comment
            tracker.track('WISDOM_COMMENT', { articleId: article.id, title: article.title });
            
            toast.success("Your thought has joined the sanctuary");
        } catch (err) {
            toast.error("Your voice was lost in the wind");
        } finally {
            setSubmittingComment(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-spiritual-white dark:bg-gray-950">
                <div className="flex flex-col items-center gap-4">
                    <div className="w-12 h-12 border-4 border-brand-blue/20 border-t-brand-blue rounded-full animate-spin" />
                    <span className="text-gray-500 font-medium animate-pulse italic">Accessing Wisdom...</span>
                </div>
            </div>
        );
    }

    if (!article) return null;

    return (
        <div className="min-h-screen w-full min-w-0 flex flex-col items-center bg-white dark:bg-gray-950 transition-colors duration-500 pb-20 overflow-x-hidden">
            {/* Dynamic SEO */}
            <SEO
                title={article.title}
                description={article.excerpt || article.content.replace(/<[^>]*>/g, '').substring(0, 160)}
                keywords={article.keywords} // Pass manual keywords
                ogImage={article.coverImage}
                ogType="article"
                url={`/article/${article.slug || article.id}`}
                schema={{
                    "@context": "https://schema.org",
                    "@type": "Article",
                    "headline": article.title,
                    "image": article.coverImage ? [article.coverImage] : [],
                    "datePublished": article.createdAt,
                    "dateModified": article.updatedAt,
                    "author": [{
                        "@type": "Person",
                        "name": article.author?.name || "All-Seer",
                        "url": `${CONFIG.SITE_URL}/explore`
                    }],
                    "publisher": {
                        "@type": "Organization",
                        "name": "Antaryatra",
                        "logo": {
                            "@type": "ImageObject",
                            "url": `${CONFIG.SITE_URL}/AntarYatra_light_icon_only.png`
                        }
                    },
                    "description": article.excerpt || article.content.replace(/<[^>]*>/g, '').substring(0, 160),
                    "keywords": article.keywords // Also add to Schema.org
                }}
            />

            {/* Main Centered Column - Pure & Restricted */}
            <main className="w-full min-w-0 max-w-2xl lg:max-w-3xl px-6 md:px-0 py-12 md:py-24 mx-auto antialiased">
                
                {/* Back Link */}
                <div className="mb-12">
                    <Link to="/explore" className="group flex items-center gap-2 text-gray-500 hover:text-brand-blue transition-colors w-fit">
                        <ArrowLeft className="w-4 h-4 transition-transform group-hover:-translate-x-1" />
                        <span className="text-sm font-medium">{t("wisdom_back")}</span>
                    </Link>
                </div>

                {/* Article Header */}
                <header className="mb-16">
                    {article.category && (
                        <div className="flex items-center gap-2 mb-6">
                            <span className="text-[10px] font-bold text-brand-blue bg-blue-50 dark:bg-blue-900/40 px-3 py-1 rounded-full uppercase tracking-widest border border-brand-blue/10">
                                {article.category.label}
                            </span>
                        </div>
                    )}
                    <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-8">
                        <h1 className="text-3xl md:text-5xl font-display font-bold text-gray-900 dark:text-gray-100 leading-[1.15] tracking-tight">
                            {article.title}
                        </h1>

                        {/* Language Switcher */}
                        {article.availableLocales?.length > 1 && (
                            <div className="flex bg-gray-100/50 dark:bg-white/5 p-1 rounded-xl border border-gray-100 dark:border-white/10 shrink-0 h-fit self-start md:self-end">
                                {["en", "hi"].map(l => (
                                    <button
                                        key={l}
                                        onClick={() => switchLanguage(l)}
                                        className={`px-4 py-1.5 rounded-lg text-[10px] font-black tracking-widest transition-all ${
                                            language === l 
                                            ? "bg-white dark:bg-gray-800 text-gray-900 dark:text-white shadow-sm" 
                                            : "text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                                        }`}
                                    >
                                        {l === "en" ? "ENGLISH" : "HINGLISH"}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    <div className="flex items-center justify-between py-6 border-y border-gray-100 dark:border-gray-800">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-brand-gradient p-[1px]">
                                <div className="w-full h-full rounded-full bg-white dark:bg-gray-900 flex items-center justify-center overflow-hidden">
                                    {article.author?.name === 'All-Seer' ? (
                                        <img src="/favicon/favicon.svg" alt="All-Seer" className="w-6 h-6 object-contain p-1" />
                                    ) : (
                                        <User className="w-5 h-5 text-gray-400" />
                                    )}
                                </div>
                            </div>
                            <div>
                                <div className="font-semibold text-sm text-gray-900 dark:text-gray-100">
                                    {(article.author?.name?.toLowerCase().includes('joshi ayush') || article.author?.name?.toLowerCase().includes('ayush joshi')) ? 'All-Seer' : (article.author?.name || 'All-Seer')}
                                </div>
                                <div className="text-[10px] text-gray-500 font-medium uppercase tracking-wider flex items-center gap-2">
                                    <span>{new Date(article.createdAt).toLocaleDateString()}</span>
                                    <span className="w-1 h-1 rounded-full bg-gray-300" />
                                    <span>{article.views || 0} reflections</span>
                                </div>
                            </div>
                        </div>
                        
                        <div className="flex items-center gap-5">
                            <motion.button 
                                whileTap={{ scale: 1.4, rotate: -15 }}
                                onClick={handleLike}
                                className={`flex items-center gap-1.5 transition-all duration-300 ${hasLiked ? 'text-red-500' : 'text-gray-400 hover:text-red-500'}`}
                            >
                                <Heart className={`w-5 h-5 ${hasLiked ? 'fill-current' : ''}`} />
                                <span className="text-xs font-bold">{article.likes || 0}</span>
                            </motion.button>
                            <motion.button 
                                whileTap={{ scale: 0.9 }}
                                onClick={handleShare}
                                className="text-gray-400 hover:text-brand-blue transition-colors p-1"
                            >
                                <Share2 className="w-5 h-5" />
                            </motion.button>
                        </div>
                    </div>
                </header>

                {/* Fallback Banner */}
                {article.fallbackUsed && (
                    <motion.div 
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="mb-12 p-4 bg-amber-50/50 dark:bg-amber-900/10 border border-amber-200/50 dark:border-amber-500/20 rounded-2xl flex items-center gap-4"
                    >
                        <div className="w-10 h-10 rounded-full bg-amber-100 dark:bg-amber-500/20 flex items-center justify-center shrink-0">
                            <Sparkles className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                        </div>
                        <div>
                            <p className="text-sm font-bold text-amber-900 dark:text-amber-400 leading-tight">{t("wisdom_fallback_title")}</p>
                            <p className="text-[10px] text-amber-700/70 dark:text-amber-400/60 font-medium">{t("wisdom_fallback_body")}</p>
                        </div>
                    </motion.div>
                )}

                {/* Article Content */}
                <article className="prose prose-lg md:prose-xl prose-blue dark:prose-invert max-w-none font-serif leading-relaxed text-gray-800 dark:text-gray-200 selection:bg-brand-blue/20">
                    {article.coverImage && (
                        <div className="relative mb-8 rounded-2xl overflow-hidden shadow-2xl shadow-blue-900/5">
                            <img src={article.coverImage} className="w-full h-auto" alt={article.title} />
                        </div>
                    )}

                    {/* Sacred Audio Player */}
                    {article.audioUrl && (
                        <div className="mb-14">
                            <SacredAudioPlayer src={article.audioUrl.startsWith("/") ? `${CONFIG.API_HOST}${article.audioUrl}` : article.audioUrl} />
                        </div>
                    )}

                    <div 
                        className="article-body first-letter:text-5xl first-letter:font-bold first-letter:text-brand-blue first-letter:mr-3 first-letter:float-left" 
                        dangerouslySetInnerHTML={{ __html: article.content }} 
                    />
                </article>

                {/* End Section Divider */}
                <div className="my-24 flex items-center justify-center gap-4">
                    <div className="w-12 h-[1px] bg-gray-200 dark:bg-gray-800" />
                    <Sparkles className="w-6 h-6 text-gray-200 dark:bg-gray-800" />
                    <div className="w-12 h-[1px] bg-gray-200 dark:bg-gray-800" />
                </div>

                {/* Discussion */}
                <section className="mt-20">
                    <h3 className="text-2xl font-display font-bold text-gray-900 dark:text-gray-100 flex items-center gap-3 mb-10">
                        <MessageCircle className="w-6 h-6 text-brand-blue" /> {t("wisdom_reflections")}
                    </h3>

                    {user ? (
                        <div className="mb-12 bg-gray-50/50 dark:bg-gray-900/50 p-6 rounded-3xl border border-gray-100 dark:border-gray-800">
                            <textarea
                                value={commentText}
                                onChange={(e) => setCommentText(e.target.value)}
                                placeholder="What resonance did this leave within you?"
                                className="w-full p-5 rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 outline-none h-32 resize-none text-sm transition-focus focus:ring-4 focus:ring-brand-blue/5"
                            />
                            <div className="flex justify-end mt-4">
                                <button
                                    onClick={handlePostComment}
                                    disabled={!commentText.trim() || submittingComment}
                                    className="px-8 py-3 bg-brand-blue text-white rounded-xl font-bold hover:shadow-lg hover:shadow-blue-500/20 disabled:opacity-50 transition-all flex items-center gap-2"
                                >
                                    <Send className="w-4 h-4" /> Share Thought
                                </button>
                            </div>
                        </div>
                    ) : (
                        <div className="mb-12 p-10 rounded-3xl border border-dashed border-gray-200 dark:border-gray-800 text-center bg-gray-50/30 dark:bg-gray-900/20">
                            <Sparkles className="w-8 h-8 text-brand-blue/30 mx-auto mb-4" />
                            <p className="text-gray-500 dark:text-gray-400 font-medium italic mb-6">
                                Your thoughts are precious. Login to join the sanctuary dialogue.
                            </p>
                            <Link 
                                to={`/login?redirect=${encodeURIComponent(window.location.pathname)}`}
                                className="inline-flex items-center gap-2 px-8 py-3 bg-white dark:bg-gray-800 text-brand-blue border border-brand-blue/20 rounded-xl font-bold hover:bg-brand-blue hover:text-white transition-all shadow-sm"
                            >
                                <User className="w-4 h-4" /> Sign In to Reflect
                            </Link>
                        </div>
                    )}

                    <div className="space-y-6">
                        {article.comments?.map((comment) => (
                            <div key={comment.id} className="p-6 rounded-2xl border border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-900">
                                <div className="flex justify-between items-center mb-3">
                                    <div className="flex items-center gap-2">
                                        {comment.author?.name === 'All-Seer' ? (
                                            <div className="w-5 h-5 rounded-full bg-brand-blue/10 flex items-center justify-center overflow-hidden">
                                                <img src="/favicon/favicon.svg" alt="" className="w-3 h-3 object-contain" />
                                            </div>
                                        ) : (
                                            <div className="w-5 h-5 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
                                                <User className="w-3 h-3 text-gray-400" />
                                            </div>
                                        )}
                                        <span className="font-bold text-gray-900 dark:text-gray-100 text-sm">
                                            {(comment.author?.name?.toLowerCase().includes('joshi ayush') || comment.author?.name?.toLowerCase().includes('ayush joshi')) ? 'All-Seer' : (comment.author?.name || 'Anonymous')}
                                        </span>
                                    </div>
                                    <span className="text-[10px] text-gray-400 font-bold uppercase tracking-widest">{new Date(comment.createdAt).toLocaleDateString()}</span>
                                </div>
                                <p className="text-gray-700 dark:text-gray-300 text-sm leading-relaxed">{comment.content}</p>
                            </div>
                        ))}
                    </div>
                </section>

                {/* Recommendations */}
                {recommendations.length > 0 && (
                    <section className="mt-32 pt-20 border-t border-gray-100 dark:border-gray-800">
                        <h3 className="text-2xl font-display font-bold text-gray-900 dark:text-gray-100 mb-10">{t("wisdom_further")}</h3>
                        <div className="grid md:grid-cols-2 gap-8">
                            {recommendations.slice(0, 2).map(rec => (
                                <Link to={`/article/${rec.slug || rec.id}`} key={rec.id} className="group block space-y-4">
                                    {rec.coverImage && (
                                        <div className="aspect-video w-full rounded-2xl overflow-hidden bg-gray-100 dark:bg-gray-900">
                                            <img src={rec.coverImage} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" alt="" />
                                        </div>
                                    )}
                                    <h4 className="font-bold text-xl text-gray-900 dark:text-gray-100 group-hover:text-brand-blue transition-colors line-clamp-2">{rec.title}</h4>
                                </Link>
                            ))}
                        </div>
                    </section>
                )}
            </main>
        </div>
    );
}

// --- Internal Sacred Audio Player ---
function SacredAudioPlayer({ src }) {
    const [playing, setPlaying] = useState(false);
    const [progress, setProgress] = useState(0);
    const [duration, setDuration] = useState(0);
    const [loading, setLoading] = useState(true);
    const audioRef = React.useRef(null);

    useEffect(() => {
        const audio = audioRef.current;
        if (!audio) return;

        const updateProgress = () => {
            if (audio.duration) {
                setProgress((audio.currentTime / audio.duration) * 100);
            }
        };

        const onLoadedMetadata = () => {
            setLoading(false);
            setDuration(audio.duration);
        };

        const onEnded = () => {
            setPlaying(false);
            setProgress(0);
        };

        const onCanPlay = () => setLoading(false);

        audio.addEventListener("timeupdate", updateProgress);
        audio.addEventListener("loadedmetadata", onLoadedMetadata);
        audio.addEventListener("ended", onEnded);
        audio.addEventListener("canplay", onCanPlay);

        return () => {
            audio.removeEventListener("timeupdate", updateProgress);
            audio.removeEventListener("loadedmetadata", onLoadedMetadata);
            audio.removeEventListener("ended", onEnded);
            audio.removeEventListener("canplay", onCanPlay);
        };
    }, [src]);

    const togglePlay = () => {
        if (!audioRef.current) return;
        if (playing) {
            audioRef.current.pause();
        } else {
            audioRef.current.play().catch(e => console.error(e));
        }
        setPlaying(!playing);
    };

    const formatTime = (time) => {
        if (!time || isNaN(time)) return "0:00";
        const min = Math.floor(time / 60);
        const sec = Math.floor(time % 60);
        return `${min}:${sec < 10 ? "0" : ""}${sec}`;
    };

    const handleProgressChange = (e) => {
        const newProgress = parseFloat(e.target.value);
        if (audioRef.current && audioRef.current.duration) {
            audioRef.current.currentTime = (newProgress / 100) * audioRef.current.duration;
            setProgress(newProgress);
        }
    };

    return (
        <div className="bg-gray-50/50 dark:bg-gray-900/50 backdrop-blur-sm border border-gray-100 dark:border-gray-800 rounded-2xl p-6 flex flex-col md:flex-row items-center gap-6 shadow-sm">
            <audio ref={audioRef} src={src} preload="metadata" crossOrigin="anonymous" />
            
            <button
                onClick={togglePlay}
                disabled={loading}
                className="w-14 h-14 flex-shrink-0 bg-brand-blue text-white rounded-full flex items-center justify-center hover:scale-105 active:scale-95 transition-all shadow-lg shadow-blue-500/20 disabled:opacity-50"
            >
                {loading ? (
                    <div className="w-5 h-5 border-2 border-white/20 border-t-white rounded-full animate-spin" />
                ) : playing ? (
                    <Pause className="w-6 h-6 fill-current" />
                ) : (
                    <Play className="w-6 h-6 fill-current ml-1" />
                )}
            </button>

            <div className="flex-1 w-full space-y-2">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-brand-blue font-bold text-xs uppercase tracking-widest">
                        <Headphones className="w-3.5 h-3.5" /> Direct Wisdom Transmission
                    </div>
                    <div className="text-[10px] font-bold text-gray-400 font-mono">
                        {formatTime(audioRef.current?.currentTime)} / {formatTime(duration)}
                    </div>
                </div>

                <div className="relative h-2 w-full group">
                    <input
                        type="range"
                        min="0"
                        max="100"
                        value={progress}
                        onChange={handleProgressChange}
                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                    />
                    <div className="h-full bg-gray-200 dark:bg-gray-800 rounded-full overflow-hidden">
                        <motion.div 
                            initial={{ width: 0 }}
                            animate={{ width: `${progress}%` }}
                            className="h-full bg-brand-blue relative"
                        >
                            <div className="absolute right-0 top-1/2 -translate-y-1/2 w-3 h-3 bg-white border-2 border-brand-blue rounded-full shadow-md scale-0 group-hover:scale-100 transition-transform" />
                        </motion.div>
                    </div>
                </div>
            </div>
        </div>
    );
}
